/**
 * Tools for handling authentication JWTs
 */

interface IJWTBody {
  iss: string;
  useruuid: string;
  iat: number;
  exp: number;
  jti: string;
  userfirstnames: string;
  userlastnames: string;
  usergroups: string[];
  orgid: string;
  idpUrl: string | null;
}

const jwtSearchExpression = /neurojwt=([^;]*);?/;

const atobUtf8 = (str: string): string => {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
};

/**
 * Parse neurojwt cookie contents from document.cookie
 * @param {string} cookie - Optional cookie, otherwise use document.cookie
 * @return {string|false} Return neurojwt cookie contents, or false if theres nothing
 */
export const parseJWTFromCookie = (cookie: string = document.cookie): string | false => {
  const matchArray = cookie.match(jwtSearchExpression);
  return matchArray && matchArray.length > 0 ? matchArray[1] : false;
};

const parseJSONFromJWT = (JWT: string | false): IJWTBody | false => {
  if (JWT) return JSON.parse(atobUtf8(JWT.split('.')[1]));
  else return false;
};

/**
 * Check expiration on jwt
 * @param {string} cookie - Optional cookie, otherwise use document.cookie
 * @return {true|false} Return boolean indicating if the jwt is still valid
 */
export const checkJWTNotExpired = (cookie: string = document.cookie): boolean => {
  const jwtData = parseJSONFromJWT(parseJWTFromCookie(cookie));
  return jwtData && jwtData.exp > Date.now();
};

/**
 * Return JWT data
 * @param {string} cookie - Optional cookie, otherwise use document.cookie
 * @return {IJWTBody|false} JWTBody, or false
 */
export const getJWTDasta = (cookie: string = document.cookie): IJWTBody | undefined =>
  parseJSONFromJWT(parseJWTFromCookie(cookie)) || undefined;
